import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { can, rupees, fmtDate } from "@/lib/helpers";
import { PageHeader, StatusBadge, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Check, X } from "@phosphor-icons/react";

export default function Discounts() {
  const { user, activeBranch } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ requested_percent: "", amount_basis: "", customer_id: "", reason: "" });
  const canApprove = can(user, "discounts", "discount_approve");
  const canCreate = can(user, "discounts", "create");

  const load = async () => setRows((await api.get("/discounts/requests")).data);
  useEffect(() => { load(); api.get("/customers").then(({ data }) => setCustomers(data)); }, []);

  const submit = async () => {
    try {
      await api.post("/discounts/requests", {
        branch_id: activeBranch, customer_id: form.customer_id || null,
        requested_percent: Number(form.requested_percent), amount_basis: Number(form.amount_basis || 0), reason: form.reason,
      });
      toast.success("Approval request raised");
      setOpen(false); setForm({ requested_percent: "", amount_basis: "", customer_id: "", reason: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const decide = async (id, decision) => {
    try {
      await api.put(`/discounts/requests/${id}/decision?decision=${decision}`);
      toast.success(`Request ${decision}d`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Discount Management" subtitle="Tiered approval workflow for over-limit discounts.">
        {canCreate && <Button onClick={() => setOpen(true)} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="add-discount-req"><Plus size={16} className="mr-1" /> Request Approval</Button>}
      </PageHeader>

      <div className="mb-4 text-sm text-[#8A6D2F] bg-[#FFF8E1] rounded-md px-4 py-2 inline-block">Your discount limit: <b>{user?.max_discount_percent}%</b></div>

      <Card className="bg-white border-[#E6E2D6] shadow-sm">
        {rows.length === 0 ? <EmptyState label="No discount requests" /> : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Requested By</TableHead><TableHead className="text-right">Percent</TableHead><TableHead className="text-right">Basis ₹</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`disc-row-${r.id}`}>
                  <TableCell className="font-medium">{r.requested_by_name}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium text-[#8A6D2F]">{r.requested_percent}%</TableCell>
                  <TableCell className="text-right tabular-nums">{rupees(r.amount_basis)}</TableCell>
                  <TableCell className="text-sm text-[#4A4A4A]">{r.reason || "-"}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-sm text-[#8A8A8A]">{fmtDate(r.created_at)}</TableCell>
                  <TableCell>
                    {r.status === "pending" && canApprove && (
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => decide(r.id, "approve")} className="text-[#2D6A4F]" data-testid={`approve-${r.id}`}><Check size={16} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => decide(r.id, "reject")} className="text-[#9D2A2A]" data-testid={`reject-${r.id}`}><X size={16} /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">Request Discount Approval</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#6B6B6B]">Discount %</Label><Input type="number" value={form.requested_percent} onChange={(e) => setForm({ ...form, requested_percent: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="dr-percent" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Bill Amount ₹</Label><Input type="number" value={form.amount_basis} onChange={(e) => setForm({ ...form, amount_basis: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="dr-amount" /></div>
            <div className="col-span-2">
              <Label className="text-xs text-[#6B6B6B]">Customer (optional)</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="dr-customer"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white max-h-56">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs text-[#6B6B6B]">Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="dr-reason" /></div>
          </div>
          <DialogFooter><Button onClick={submit} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="dr-save">Submit Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
